import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import './index.css';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, reauthenticateWithPopup, onAuthStateChanged, connectAuthEmulator, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, collection, connectFirestoreEmulator, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import ReactMarkdown from 'react-markdown';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import TermsOfService from './pages/TermsOfService';
import DocPreview from './pages/DocPreview';
import { debounce } from 'lodash';

// Import Catalyst components
import { Button } from './components/button';
import { Input } from './components/input';
import { Checkbox, CheckboxField, CheckboxLabel } from './components/checkbox';
import { Text } from './components/text';
import { Heading, Subheading } from './components/heading';
import { Badge } from './components/badge';
import { Field, FieldGroup, Label} from './components/fieldset';
import { Divider } from './components/divider';
import {Heading2} from "lucide-react";
import { CheckIcon, QuestionMarkCircleIcon, ChevronRightIconm, XMarkIcon, ArrowTopRightOnSquareIcon, BanknotesIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { CloudIcon, ClockIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { Switch } from '@headlessui/react'

// Add these imports at the top
import Autocomplete from 'react-google-autocomplete';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'

import {
  UnifyIntentClient,
  UnifyIntentClientConfig,
  UnifyIntentProvider,
  useUnifyIntent
} from '@unifygtm/intent-react';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

// Import the new component
import ProjectTransfer from './pages/ProjectTransfer';

// Add this constant with your other constants
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

const COUNTRIES = [
  { code: 'US', name: 'United States', states: US_STATES },
  { code: 'CA', name: 'Canada', states: CANADIAN_PROVINCES }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}
// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBJl_dNCM7ZPByF7bGR1agLWEGfQOB1iwM",
  authDomain: "get.kloudstax.com",
  projectId: "kloudstax-enrollment",
  storageBucket: "kloudstax-enrollment.appspot.com",
  messagingSenderId: "610735508352",
  appId: "1:610735508352:web:22552a1934b2e55c8fcf65",
  measurementId: "G-447J6KSVKQ"
};

const DEFAULT_TEAM_MEMBERS = [
  {
    name: 'Vinay Thakker',
    role: 'Field CTO\nKloudStax',
    imageUrl:
      'https://storage.googleapis.com/ks-enrollment-public-assets/avatars/vinay.webp',
      email: 'vinay@kloudstax.com'
  },
  {
    name: 'Jon Bitz',
    role: 'Managing Partner\nKloudStax',
    imageUrl:
      'https://storage.googleapis.com/ks-enrollment-public-assets/avatars/jonbitz.webp',
    email: 'jonbitz@kloudstax.com'
  },
];

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);
if (window.location.hostname === 'localhost') {
  connectFirestoreEmulator(db, 'localhost', 8081);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  // connectAuthEmulator(auth, "http://localhost:9099");
}

const CompanyName = ({ className = "" }) => (
  <>
    Kloud<u className={`decoration-cyan-300 decoration-3 underline-offset-4 ${className}`}>Stax</u>
  </>
);

// First, create a new AuthContext
const AuthContext = React.createContext(null);

// Create a provider component
const AuthProvider = ({ children }) => {
  const [credential, setCredential] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const ensureCredential = useCallback(async (forceLogin = false) => {
    // If we already have a valid credential, return it
    if (credential?.accessToken && !forceLogin) {
      return credential;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const user = auth.currentUser;
      const provider = new GoogleAuthProvider();
      if (forceLogin) {
        provider.setCustomParameters({
          prompt: 'select_account'
        });
      }
      
      let result;
      result = await signInWithPopup(auth, provider);
      

      const newCredential = GoogleAuthProvider.credentialFromResult(result);
      setCredential(newCredential);
      return newCredential;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [credential]);

  return (
    <AuthContext.Provider value={{ credential, isLoading, error, ensureCredential }}>
      {children}
    </AuthContext.Provider>
  );
};

// Create a custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Add this utility function near the top of the file, after the imports
const splitDisplayName = (displayName) => {
  const [firstName, ...lastNameParts] = displayName?.split(' ') || ['', ''];
  const lastName = lastNameParts.join(' ');
  return [firstName, lastName];
};

// Create new EnrollmentForm component
const EnrollmentForm = ({ onSubmit, currentUser, handleGoogleSignIn, logoutToWelcome }) => {
  // Replace the manual split with the utility function
  const [firstName, lastName] = splitDisplayName(currentUser?.displayName);
  
  // Get domain from email
  const defaultDomain = currentUser?.email?.split('@')[1] || '';

  // Add initialDataLoaded state
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [showAddressValidationModal, setShowAddressValidationModal] = useState(false);
  const [addressValidationError, setAddressValidationError] = useState(null);

  const [formData, setFormData] = useState({
    companyName: '',
    companyDomain: defaultDomain,
    country: 'US',
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    primaryContactFirstName: firstName || '',
    primaryContactLastName: lastName || '',
    primaryContactEmail: currentUser?.email || '',
    primaryContactPhone: '',
    billingContactFirstName: '',
    billingContactLastName: '',
    billingContactEmail: '',
    billingContactPhone: ''
  });

  const [wizardState, setWizardState] = useState({
    sameAsPrimary: true
  });

  const [hasReadTerms, setHasReadTerms] = useState(false);

  const { credential, isLoading, error, ensureCredential } = useAuth();

  const handleFieldChange = useCallback((e) => {
    if (e.preventDefault) {
      e.preventDefault();
    }
    const { name, value } = e.target;
  
    // Domain validation for companyDomain field
    if (name === 'companyDomain') {
      const domainRegex = /^(?:[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.)+[a-zA-Z]{2,}$/;
      if (value && !domainRegex.test(value)) {
        e.target.setCustomValidity('Please enter a valid domain name (e.g., example.com)');
      } else {
        e.target.setCustomValidity('');
      }
    }
  
    // Update state
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);
  
  const saveFormDataToFirestore = useCallback(
    debounce(async (latestData, userId, sameAsPrimary) => {  // Add sameAsPrimary parameter
      if (!userId) return;
      try {
        console.debug('Saving form data to Firestore:', latestData);
        const docRef = doc(db, 'customers', userId);
        await updateDoc(docRef, {
          ...latestData,
          'provisioning_details.lastUpdated': serverTimestamp(),
          'provisioning_details.billingContactSameAsPrimary': sameAsPrimary  // Use passed parameter
        });
      } catch (error) {
        console.error('Error saving form progress:', error);
      }
    }, 1000),
    []
  );

  useEffect(() => {
    if (currentUser?.uid) {
      saveFormDataToFirestore(formData, currentUser.uid, wizardState.sameAsPrimary);  // Pass the current value
    }
  }, [formData, currentUser?.uid, saveFormDataToFirestore, wizardState]);

  // Add useEffect to load saved form data on mount
  useEffect(() => {
    const loadSavedFormData = async () => {
      try {
        const docRef = doc(db, 'customers', currentUser.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Filter out provisioning_details and only set form fields
          const savedFormData = Object.keys(formData).reduce((acc, key) => {
            if (data[key]) {
              acc[key] = data[key];
            }
            return acc;
          }, {});
          
          // Update wizard state with saved billing contact preference
          // console.log(data)
          // console.log(savedFormData)
          if (data.provisioning_details?.billingContactSameAsPrimary !== undefined) {
            setWizardState(prev => ({
              ...prev,
              sameAsPrimary: data.provisioning_details.billingContactSameAsPrimary  // Use the saved value
            }));
          }
          
          if (Object.keys(savedFormData).length > 0) {
            setFormData(prev => ({
              ...prev,
              ...savedFormData
            }));
          }
        }
        // Set initialDataLoaded to true after loading saved data
        setInitialDataLoaded(true);
      } catch (error) {
        console.error('Error loading saved form data:', error);
        // Still set initialDataLoaded to true even if there's an error
        setInitialDataLoaded(true);
      }
    };

    loadSavedFormData();
  }, [currentUser.uid]);

  // Modify the billing contact sync effect
  useEffect(() => {
    // Skip the sync if we haven't loaded the initial data yet
    if (!initialDataLoaded) return;

    if (wizardState.sameAsPrimary) {
      setFormData(prev => ({
        ...prev,
        billingContactFirstName: prev.primaryContactFirstName,
        billingContactLastName: prev.primaryContactLastName,
        billingContactEmail: prev.primaryContactEmail,
        billingContactPhone: prev.primaryContactPhone
      }));
    } else {
      // Clear billing fields when toggling off
      setFormData(prev => ({
        ...prev,
        billingContactFirstName: '',
        billingContactLastName: '',
        billingContactEmail: '',
        billingContactPhone: ''
      }));
    }
  }, [wizardState.sameAsPrimary]);

  // Modify the Switch onChange handler
  const handleSameAsPrimaryToggle = (checked) => {
    setWizardState(prev => ({ ...prev, sameAsPrimary: checked }));
  };

  const fillDummyData = (e) => {
    e.preventDefault();
    const dummyData = {
      companyName: "KloudStax Dev",
      companyDomain: "kloudstax.dev",
      country: "US",
      streetAddress1: "123 Business Lane",
      streetAddress2: "Suite 456",
      city: "San Francisco",
      state: "CA",
      zip: "94105",
      primaryContactFirstName: "John",
      primaryContactLastName: "Smith",
      primaryContactEmail: "vinay@kloudstax.dev",
      primaryContactPhone: "(555) 123-4567"
    };

    const newFormData = {
      ...dummyData,
      ...(wizardState.sameAsPrimary && {
        billingContactFirstName: dummyData.primaryContactFirstName,
        billingContactLastName: dummyData.primaryContactLastName,
        billingContactEmail: dummyData.primaryContactEmail,
        billingContactPhone: dummyData.primaryContactPhone
      })
    };

    setFormData(newFormData);
  };
  const [showResetModal, setShowResetModal] = useState(false);

  const handleReset = (e) => {
    e.preventDefault();
    setShowResetModal(true);
  };

  const confirmReset = () => {
    // Reset to initial state based on currentUser
    const [firstName, ...lastNameParts] = currentUser?.displayName?.split(' ') || ['', ''];
    const lastName = lastNameParts.join(' ');
    
    setFormData({
      companyName: '',
      companyDomain: defaultDomain,
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
      primaryContactFirstName: firstName || '',
      primaryContactLastName: lastName || '',
      primaryContactEmail: currentUser?.email || '',
      primaryContactPhone: '',
      billingContactFirstName: '',
      billingContactLastName: '',
      billingContactEmail: '',
      billingContactPhone: ''
    });

    // Reset the billing contact checkbox to default state
    setWizardState(prev => ({
      ...prev,
      sameAsPrimary: true
    }));

    setShowResetModal(false);
  };

  const cancelReset = () => {
    setShowResetModal(false);
  };

  const validateAddress = async () => {
    const { streetAddress1, streetAddress2, city, state, zip, country } = formData;
    
    try {
      // Create Geocoder instance
      const geocoder = new window.google.maps.Geocoder();
      
      // Geocode the address using structured address format
      const response = await new Promise((resolve, reject) => {
        geocoder.geocode({
          componentRestrictions: {
            route: streetAddress1,
            locality: city,
            administrativeArea: state,
            postalCode: zip,
            country: country || 'US'
          }
        }, (results, status) => {
          if (status === 'OK') {
            resolve(results);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });

      // Check if we got a valid street address
      const validAddress = response[0]?.address_components?.some(
        component => component.types.includes('street_number')
      );

      if (!validAddress) {
        throw new Error('Invalid address - please enter a complete street address');
      }

      return true;
    } catch (error) {
      console.error('Address validation error:', error);
      return {
        isValid: false,
        error: error.message
      };
    }
  };

  // Add a submission lock
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modify handleSubmit
  const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Prevent multiple submissions
      if (isSubmitting) return;
      
      try {
          setIsSubmitting(true);
          
          // Validate address
          const addressValidation = await validateAddress();
          
          if (addressValidation !== true) {
              setAddressValidationError(addressValidation.error);
              setShowAddressValidationModal(true);
              return;
          }

          // If address is valid, proceed with form submission
          await onSubmit(formData, wizardState);
      } catch (error) {
          console.error('Form submission error:', error);
          setAddressValidationError('There was an error submitting the form. Please try again.');
          setShowAddressValidationModal(true);
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleForceSubmit = async () => {
      if (isSubmitting) return;
      
      try {
          setIsSubmitting(true);
          setShowAddressValidationModal(false);
          await onSubmit(formData, wizardState);
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleReviewAddress = () => {
    setShowAddressValidationModal(false);
    // Find the address input and scroll to it
    const addressInput = document.querySelector('input[name="streetAddress1"]');
    if (addressInput) {
      addressInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      addressInput.focus();
    }
  };

  const handleTermsScroll = (e) => {
    const element = e.target;
    const isAtBottom = Math.abs(
      element.scrollHeight - element.scrollTop - element.clientHeight
    ) < 1;

    if (isAtBottom) {
      setHasReadTerms(true);
    }
  };

  // Create refs for the autocomplete instance and session token
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);

  // Initialize session token when component mounts
  useEffect(() => {
    if (window.google) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  // Create debounced address prediction function
  const debouncedAddressPredict = useCallback(
    debounce((input, autocomplete) => {
      if (!input || !autocomplete) return;
      
      autocomplete.getPlacePredictions({
        input,
        componentRestrictions: { country: 'us' },
        types: ['address'],
        sessionToken: sessionTokenRef.current
      }, (predictions, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          console.error('Autocomplete prediction failed:', status);
          return;
        }
        // Handle predictions...
      });
    }, 300),
    []
  );

  // Add place details handler
  const handlePlaceSelect = useCallback((place) => {
    if (!place) {
      console.warn("No place selected");
      return;
    }

    if (!place.geometry) {
      console.warn("Place details not found for input:", place.name);
      return;
    }

    // Initialize address components object
    let addressComponents = {
      street_number: '',
      route: '',
      locality: '',
      administrative_area_level_1: '',
      postal_code: '',
      subpremise: ''
    };

    // Extract address components
    place.address_components?.forEach(component => {
      const type = component.types[0];
      if (addressComponents.hasOwnProperty(type)) {
        if (type==="administrative_area_level_1") { // state should be short name
          addressComponents[type] = component.short_name;
        }
        else {
          addressComponents[type] = component.long_name;
        }
      }
    });

    // Update form data with extracted address
    setFormData(prev => ({
      ...prev,
      streetAddress1: `${addressComponents.street_number} ${addressComponents.route}`.trim(),
      streetAddress2: addressComponents.subpremise,
      city: addressComponents.locality,
      state: addressComponents.administrative_area_level_1,
      zip: addressComponents.postal_code
    }));
  }, []);

  // Initialize autocomplete in useEffect
  useEffect(() => {
    if (!window.google || autocompleteRef.current) return;

    try {
      const input = document.querySelector('input[name="streetAddress1"]');
      if (!input) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: formData.country.toLowerCase() },
        types: ['address'],
        fields: ['address_components', 'geometry', 'name'],
        sessionToken: sessionTokenRef.current
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        handlePlaceSelect(place);
      });

      return () => {
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error initializing Places Autocomplete:', error);
    }
  }, [handlePlaceSelect, formData.country]);

  // Move the entire renderUserForm JSX here, but rename it to just return JSX
  return (
    <div className="mx-auto max-w-4xl">
      <form 
        onSubmit={handleSubmit} 
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
          }
        }}
        className="rounded-lg bg-white p-10 shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
        
        <div className="flex items-center gap-2 mb-10">
          <div className="text-3xl">
            <div className="block text-5xl font-semibold tracking-tight text-gray-900 mb-2">
              Hi, {splitDisplayName(auth.currentUser.displayName)[0]}! 👋
            </div>
            <span className="text-lg text-gray-500">
              You are logged in with {auth.currentUser.email}
                  &nbsp;(<button 
                onClick={logoutToWelcome}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Not You?
              </button>)
            </span>
          </div>
        </div>
        <Heading>Company Information</Heading>
        <Divider className="my-10 mt-6" />

        {/* Company Information Section */}
        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center size-6 rounded-full bg-black text-white text-sm font-medium">
                1
              </div>
              <Subheading>Company Details</Subheading>
            </div>
            <Text>Basic information about your organization.</Text>
          </div>
          <div className="space-y-4">
            <Field>
              <Label>Company Name</Label>
              <Input
                name="companyName"
                value={formData.companyName}
                onChange={handleFieldChange}
                required
              />
            </Field>
            <Field>
              <Label>Company Domain</Label>
              <Input
                name="companyDomain"
                value={formData.companyDomain}
                onChange={handleFieldChange}
                // pattern="^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}$"
                title="Please enter a valid domain name (e.g., example.com)"
                required
              />
              <Text className="text-sm text-gray-500 mt-1">
                Enter your company's domain name<br />
                This is most likely <strong>{defaultDomain}</strong>
              </Text>
            </Field>
          </div>
        </section>

        <Divider className="my-10" soft />

        {/* Address Section */}
        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center size-6 rounded-full bg-black text-white text-sm font-medium">
                2
              </div>
              <Subheading>Address</Subheading>
            </div>
            


            <div>
              <Text className="mb-10">This is where your organization is registered or is headquartered.</Text>
              <div className="text-sm font-medium text-gray-400 italic mt-12">
              <QuestionMarkCircleIcon aria-hidden="true" className="mr-1.5 size-5 text-cyan-300 inline"/>
              Not in the United States or Canada? Skip this form and reach out to <a
                      className="underline text-cyan-500"
                      href="mailto:sales@kloudstax.com">sales@kloudstax.com</a> </div>
                      </div>

          </div>
          <div className="space-y-4">
            <Field>
              <Label>Country</Label>
              <div className="relative mt-2">
                <Combobox
                  as="div"
                  value={COUNTRIES.find(country => country.code === formData.country) || null}
                  onChange={(country) => {
                    handleFieldChange({
                      target: {
                        name: 'country',
                        value: country.code
                      }
                    });
                  }}
                >
                  <ComboboxInput
                    className="block w-full rounded-md bg-white py-1.5 pl-3 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm/6"
                    onChange={(event) => event.target.value}
                    displayValue={(country) => country?.name}
                    placeholder="Select a country"
                    required
                    readOnly={true}
                  />
                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
                  </ComboboxButton>

                  <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {COUNTRIES.map((country) => (
                      <ComboboxOption
                        key={country.code}
                        value={country}
                        className="group relative cursor-default select-none py-2 pl-8 pr-4 text-gray-900 data-[focus]:bg-cyan-600 data-[focus]:text-white data-[focus]:outline-none"
                      >
                        {({ selected }) => (
                          <>
                            <span className="block truncate">
                              {country.name}
                            </span>

                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-cyan-600 group-data-[focus]:text-white">
                                <CheckIcon className="size-5" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </ComboboxOption>
                    ))}
                  </ComboboxOptions>
                </Combobox>
              </div>
            </Field>
            <Field>
              <Label>Street Address 1</Label>
              <div className="relative">
                <Autocomplete
                  name="streetAddress1"
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  onPlaceSelected={handlePlaceSelect}
                  options={{
                    componentRestrictions: { country: 'us' },
                    types: ['address'],
                    fields: ['address_components', 'geometry', 'name'],
                  }}
                  className="block w-full rounded-md border-0 p-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm sm:leading-6"
                  value={formData.streetAddress1}
                  onChange={handleFieldChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
                <Text className="text-sm text-gray-500 mt-1">
                  You can either type an address manually or select from Google suggestions
                </Text>
              </div>
            </Field>
            <Field>
              <Label>Street Address 2</Label>
              <Input name="streetAddress2" value={formData.streetAddress2} onChange={handleFieldChange} />
              <Text className="text-sm text-gray-500 mt-1">
                Suite, Apartment, Building, Floor, etc.
              </Text>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <Label>City</Label>
                <Input
                    name="city"
                    value={formData.city}
                    onChange={handleFieldChange}
                    required
                />
              </Field>
              <Field>
                <Label>State/Province</Label>
                <div className="relative mt-2">
                  <Combobox
                    as="div"
                    value={(COUNTRIES.find(country => country.code === formData.country)?.states || []).find(state => state.code === formData.state) || null}
                    onChange={(state) => {
                      handleFieldChange({
                        target: {
                          name: 'state',
                          value: state.code
                        }
                      });
                    }}
                  >
                    <ComboboxInput
                      className="block w-full rounded-md bg-white py-1.5 pl-3 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm/6"
                      onChange={(event) => event.target.value}
                      displayValue={(state) => state?.name}
                      placeholder={`Select a ${formData.country === 'US' ? 'state' : 'province'}`}
                      required
                    />
                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                      <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
                    </ComboboxButton>

                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                      {(COUNTRIES.find(country => country.code === formData.country)?.states || []).map((state) => (
                        <ComboboxOption
                          key={state.code}
                          value={state}
                          className="group relative cursor-default select-none py-2 pl-8 pr-4 text-gray-900 data-[focus]:bg-cyan-600 data-[focus]:text-white data-[focus]:outline-none"
                        >
                          {({ selected }) => (
                            <>
                              <span className="block truncate">
                                {state.name} ({state.code})
                              </span>

                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-cyan-600 group-data-[focus]:text-white">
                                  <CheckIcon className="size-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </ComboboxOption>
                      ))}
                    </ComboboxOptions>
                  </Combobox>
                </div>
              </Field>
              <Field>
                <Label>ZIP Code</Label>
                <Input
                  name="zip"
                  value={formData.zip}
                  onChange={handleFieldChange}
                  required
                />
              </Field>
            </div>
          </div>
        </section>

        <Divider className="my-10" soft />

        {/* Contact Information Section */}
        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center size-6 rounded-full bg-black text-white text-sm font-medium">
                3
              </div>
              <Subheading>Authorized Contact</Subheading>
            </div>
            <Text>Contact details for the authorized account administrator. This person has the authority to make or approve changes to the company's Google Cloud account.</Text>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <Label>First Name</Label>
                <Input 
                  name="primaryContactFirstName" 
                  value={formData.primaryContactFirstName}
                  onChange={handleFieldChange}
                  required 
                />
              </Field>
              <Field>
                <Label>Last Name</Label>
                <Input 
                  name="primaryContactLastName"
                  value={formData.primaryContactLastName}
                  onChange={handleFieldChange}
                  required 
                />
              </Field>
            </div>
            <Field>
              <Label>Email</Label>
              <Input 
                type="email" 
                name="primaryContactEmail"
                value={currentUser.email}
                disabled
                className="bg-gray-100 cursor-not-allowed"
                required 
              />
              <Text className="text-sm text-gray-500 mt-1">
                Email is locked to your Google account email
              </Text>
            </Field>
            <Field>
              <Label>Phone</Label>
              <Input type="tel" name="primaryContactPhone" value={formData.primaryContactPhone} onChange={handleFieldChange} required />
            </Field>
          </div>
        </section>

        <Divider className="my-10" soft />

        {/* Billing Contact Section */}
        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center size-6 rounded-full bg-black text-white text-sm font-medium">
                4
              </div>
              <Subheading>Billing Contact</Subheading>
            </div>
             <Text>Contact details for billing and invoicing.</Text>
          </div>
          <div className="space-y-4">
            <Field>
              <Label>Same as Primary Contact</Label>
              <div className="flex items-center">
                <Switch
                  checked={wizardState.sameAsPrimary}
                  onChange={handleSameAsPrimaryToggle}
                  className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 data-[checked]:bg-cyan-400"
                >
                  <span className="sr-only">Same as primary contact</span>
                  <span className="pointer-events-none relative inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in group-data-[checked]:opacity-0 group-data-[checked]:duration-100 group-data-[checked]:ease-out"
                    >
                      <svg fill="none" viewBox="0 0 12 12" className="size-3 text-gray-400">
                        <path
                          d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 flex size-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-data-[checked]:opacity-100 group-data-[checked]:duration-200 group-data-[checked]:ease-in"
                    >
                      <svg fill="currentColor" viewBox="0 0 12 12" className="size-3 text-cyan-400">
                        <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                      </svg>
                    </span>
                  </span>
                </Switch>
              </div>
            </Field>
            

              {!wizardState.sameAsPrimary && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Field>
                      <Label>First Name</Label>
                      <Input
                        name="billingContactFirstName"
                        value={formData.billingContactFirstName}
                        onChange={handleFieldChange}
                        required={!wizardState.sameAsPrimary}
                      />
                    </Field>
                    <Field>
                      <Label>Last Name</Label>
                      <Input
                        name="billingContactLastName"
                        value={formData.billingContactLastName}
                        onChange={handleFieldChange}
                        required={!wizardState.sameAsPrimary}
                      />
                    </Field>
                  </div>
                  <Field>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      name="billingContactEmail"
                      value={formData.billingContactEmail}
                      onChange={handleFieldChange}
                      required={!wizardState.sameAsPrimary}
                    />
                  </Field>
                  <Field>
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      name="billingContactPhone"
                      value={formData.billingContactPhone}
                      onChange={handleFieldChange}
                      required={!wizardState.sameAsPrimary}
                    />
                  </Field>
                </>
              )}
             </div>
        </section>

        <Divider className="my-10" soft />

        <div className="flex justify-between gap-4">
          <div> {/* Left side */}
            <Button type="button" onClick={handleReset} plain className="cursor-pointer">
              Reset Fields
            </Button>
          </div>
          
          <div className="flex gap-4"> {/* Right side */}
            {process.env.NODE_ENV === 'development' && (
              <Button type="button" onClick={fillDummyData} color="zinc" plain className="cursor-pointer">
                Fill Test Data
              </Button>
            )}
            <Button 
                type="submit" 
                className="bg-black text-white hover:bg-black cursor-pointer hover:text-cyan-300"
                color="black"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Submitting...' : 'Enroll Me'}
            </Button>
          </div>
        </div>

      </form>

      {/* Move modal outside the form but inside the component's root div */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-zinc-800 p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg mb-4">Reset Form?</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Are you sure you want to reset the form? All entered information will be cleared.
            </p>
            <div className="flex justify-between gap-4">
              <Button
                type="button"
                onClick={confirmReset}
                className="text-white"
                color="red"
              >
                Yes, clear the fields
              </Button>
              <Button
                type="button"
                onClick={cancelReset}
                className="bg-black hover:bg-gray-900 text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Address Validation Modal */}
      <ConfirmationModal
        open={showAddressValidationModal}
        onClose={() => setShowAddressValidationModal(false)}
        title="Address Validation Warning"
        message={
          <div className="text-sm text-gray-500">
            <p>Google could not validate the address you entered. This may cause the creation of your account with Google to fail and slow down the enrollment process. If it does fail, our team will be automatically notified and will contact you over email to help.</p>
            <p className="mt-4">Would you like to review your address or proceed anyway?</p>
          </div>
        }
        primaryButtonText="Let me review"
        onPrimaryAction={handleReviewAddress}
        secondaryButtonText="Proceed anyway"
        onSecondaryAction={handleForceSubmit}
        icon={<ExclamationTriangleIcon aria-hidden="true" className="size-6 text-yellow-600" />}
        iconBgColor="bg-yellow-100"
      />
    </div>
  );
};

// Add this new component after the CompanyName component
const GmailBlockPage = ({ currentUser, logoutToWelcome, handleGoogleSignIn }) => {
  const [firstName] = splitDisplayName(currentUser?.displayName);
  
  const handleSwitchAccount = async () => {
    try {
      await handleGoogleSignIn(true); // Pass true to force a new login
    } catch (error) {
      console.error('Error during forced sign-in:', error);
    }
  };
  
  return (
    <div className="mx-auto max-w-4xl">
      <div className="rounded-lg bg-white p-10 shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
        <div className="flex items-center gap-2 mb-10">
          <div className="text-3xl">
            <div className="block text-5xl font-semibold tracking-tight text-gray-900 mb-2">
              Hi, {firstName}! 👋
            </div>
            <span className="text-lg text-gray-500">
              You are logged in with {currentUser.email}
              &nbsp;(<button 
                onClick={handleSwitchAccount}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Switch Account
              </button>)
            </span>
          </div>
        </div>

        <div className="rounded-lg border-l-4 border-yellow-400 bg-yellow-50 p-4 dark:bg-yellow-400/20">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                Company Email Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>
                  The KloudStax Benefits Program is only available for company email addresses. 
                  Personal Gmail accounts cannot be used for enrollment.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          <div>
            <Heading>What should I do?</Heading>
            <div className="mt-4 space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 size-6 mt-1">
                  <span className="flex size-6 items-center justify-center rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-800 dark:text-cyan-100">
                    1
                  </span>
                </div>
                <div className="ml-4">
                  <Text className="font-medium">Sign in with your company email</Text>
                  <Text className="text-gray-500">
                    Use your work email address to access the enrollment form.
                  </Text>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 size-6 mt-1">
                  <span className="flex size-6 items-center justify-center rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-800 dark:text-cyan-100">
                    2
                  </span>
                </div>
                <div className="ml-4">
                  <Text className="font-medium">Don't have a company email?</Text>
                  <Text className="text-gray-500">
                    Contact <a href="mailto:sales@kloudstax.com" className="text-cyan-600 hover:text-cyan-500 underline">sales@kloudstax.com</a> to discuss alternative enrollment options.
                  </Text>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleSwitchAccount}
              className="bg-black text-white cursor-pointer"
            >
              Switch to Company Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [wizardState, setWizardState] = useState({
    userAcceptedTerms: false,
    userScrolledToEnd: false,
    selectedProjects: {}, 
    sameAsPrimary: true,
  });
  const [projects, setProjects] = useState([]);
  const [switchingProgress, setSwitchingProgress] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [userCredential, setUserCredential] = useState(null);
  const { credential, isLoading, error, ensureCredential } = useAuth();
  const [provisioningStatus, setProvisioningStatus] = useState(null);
  const [provisioningDetails, setProvisioningDetails] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersError, setTeamMembersError] = useState(null);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(true);
  const [termsContent, setTermsContent] = useState('');
  const unify = useUnifyIntent();

  useEffect(() => {
    fetch('/content/terms-of-service.md')
      .then(response => response.text())
      .then(content => setTermsContent(content))
      .catch(error => console.error('Error loading terms:', error));
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.debug("Auth state changed");
      setCurrentUser(user);
      if (user) {
        if (isGmailAccount(user.email)) {
          setCurrentStep('gmail-block');
        } else {
          checkIfUserAlreadySubmitted(user);
        }
      }
    });

    return () => unsubscribe();
  }, [isGmailAccount, setCurrentStep]);

  useEffect(() => {
    console.debug("CurrentStep changed to ", currentStep);
  }, [currentStep]);

  const checkIfUserAlreadySubmitted = async (user) => {
    try {
      console.debug("checkIfUserAlreadySubmitted called");
      const uid = user.uid;
      const docRef = doc(db, 'customers', uid);
      const docSnap = await getDoc(docRef);
      const provisioningDetails = docSnap.data()?.provisioning_details;

      // if gmail account, set current step to gmail-block
      if (isGmailAccount(user.email||"")) {
        setCurrentStep('gmail-block');
        return;
      }

      if (provisioningDetails) {
        setProvisioningDetails(provisioningDetails);
      }

      if (!docSnap.exists() || !docSnap.data()?.provisioning_details?.tos_accepted_at) {
       setCurrentStep('terms');
      }
      else if (docSnap.data()?.provisioning_details?.status === 'completed') {
        setCurrentStep('done');
      }

      else if (docSnap.provisioning_details?.billing_account) {
        setCurrentStep('congratulations');
      }
      else {
        setWizardState(prev => ({
          ...prev,
          userAcceptedTerms: true
        }));
        setCurrentStep('form'); // default assumes an invalid form cannot be submitted & they are immutable
      }

    } catch (error) {
      console.error("Error checking user submission:", error);
    }
  };

  const handleGoogleSignIn = async (forceLogin = false) => {
    try {
      console.debug("handleGoogleSignIn called with forceLogin = ", forceLogin);
      const credential = await ensureCredential(forceLogin);
      setCurrentUser(auth.currentUser);
      await checkIfUserAlreadySubmitted(auth.currentUser);
    } catch (error) {
      console.error("Error during sign-in:", error);
    }
  };

  const handleTermsScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight) {
      setWizardState(prev => ({ ...prev, userScrolledToEnd: true }));
    }
  };

  const handleTermsAccept = async () => {
    try {
      // Fetch IP address from ipify API
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const { ip } = await ipResponse.json();

      // Get current document
      const docRef = doc(db, 'customers', auth.currentUser.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // For new documents, set initial structure
        const [firstName, lastName] = splitDisplayName(auth.currentUser.displayName);

        await setDoc(docRef, {
          primaryContactEmail: auth.currentUser.email,
          primaryContactFirstName: firstName,
          primaryContactLastName: lastName,
          provisioning_details: {
            status: 'draft',
            tos_accepted_at: serverTimestamp(),
            tos_acceptance_ip: ip,
            tos_acceptance_url: window.location.href
          }
        });
      } else if (!docSnap.data()?.provisioning_details?.tos_accepted_at) {
        // For existing documents, update specific fields using dot notation
        await updateDoc(docRef, {
          'provisioning_details.status': 'draft',
          'provisioning_details.tos_accepted_at': serverTimestamp(),
          'provisioning_details.tos_acceptance_ip': ip,
          'provisioning_details.enrollment_url': window.location.href,
          'provisioning_details.lastUpdated': serverTimestamp(),
        });
      }

      setCurrentStep('form');
    } catch (error) {
      console.error('Error updating terms acceptance:', error);
    }
  };

  // Checkbox now only controls local state
  const handleTermsCheckbox = (e) => {
    setWizardState(prev => ({
      ...prev,
      userAcceptedTerms: !prev.userAcceptedTerms
    }));
  };

  const handleProjectChange = (projectId) => {
  setWizardState(prev => ({
    ...prev,
    selectedProjects: {
      ...prev.selectedProjects,
      [projectId]: !prev.selectedProjects[projectId]
    }
  }));
};


const logoutToWelcome = async () => {
  try {
    await signOut(auth);
    setCurrentStep('welcome');
    
  } catch (error) {
    console.error('Error during logout:', error);
  }
}

  const handleFormSubmit = async (formData, formWizardState) => {
    try {
      setCurrentStep('provisioning');
      
      // Get current document to preserve existing provisioning_details
      const docRef = doc(db, 'customers', currentUser.uid);
      const docSnap = await getDoc(docRef);
      const existingProvisioningDetails = docSnap.exists() ? 
        docSnap.data()?.provisioning_details || {} : {};

      // Merge existing provisioning_details with new values
      await setDoc(docRef, {
        ...formData,
        provisioning_details: {
          ...existingProvisioningDetails,
          status: 'ready_to_process',
          lastUpdated: serverTimestamp(),
          teamMembers: teamMembers,
          billingContactSameAsPrimary: formWizardState.sameAsPrimary
        }
      });
    } catch (error) {
      console.error("Error saving user data:", error);
    }
  };

  const handleUpdateIAM = async(e) => {
    try {
      const cred = await ensureCredential();
      const getOrgId = async () => {
        const response = await fetch('https://cloudresourcemanager.googleapis.com/v3/organizations:search', {
          headers: {
            'Authorization': `Bearer ${cred.accessToken}`,
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch organizations');
        }

        const data = await response.json();
        // Get first organization ID associated with user
        const orgId = data.organizations[0].name.split('/')[1];
        return orgId;
      };
      const orgId = await getOrgId();
      
      const policy = {
        bindings: [
          {
            role: 'roles/billing.admin',
            members: [`user:${currentUser.email}`]
          },
          {
            role: 'roles/owner', 
            members: [`user:${currentUser.email}`]
          }
        ]
      };

      const functions = getFunctions();
      const proxyIAMPolicy = httpsCallable(functions, 'proxyIAMPolicy');
      
      const result = await proxyIAMPolicy({
        orgId,
        policy,
        accessToken: cred.accessToken
      });

      console.log('Successfully updated organization IAM roles:', result.data);
      setCurrentStep('projects');

    } catch (error) {
      console.error('Failed to update IAM policy:', error);
    }
  };

  const handleProjectSwitch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const selectedProjects = formData.getAll('projects');
    const ksdev_bid = "billingAccounts/01F164-F467CC-F1A684"
    const acme_bid = "billingAccounts/019D49-4F9707-5319AB"
    assignBillingAccounts(selectedProjects, ksdev_bid)
    // selectedProjects.forEach((projectId, index) => {
    //   setTimeout(() => {
    //     setSwitchingProgress(prev => [...prev, projectId]);
    //     if (index === selectedProjects.length - 1) {
    //       setCurrentStep('congratulations');
    //     }
    //   }, 1000 * (index + 1));
  };


  const addTestProjectsOld = () => {
    const testProjects = [
      { projectId: 'project-123456', projectName: 'Production Environment' },
      { projectId: 'project-789012', projectName: 'Development Environment' },
      { projectId: 'project-345678', projectName: 'Staging Environment' },
      { projectId: 'project-901234', projectName: 'Data Analytics Platform' },
      { projectId: 'project-567890', projectName: 'Mobile App Backend' }
    ];
    setProjects(testProjects);

    // Initialize all projects as selected in the wizard state
    const initialSelections = {};
    testProjects.forEach(project => {
      initialSelections[project.projectId] = true;
    });
    setWizardState(prev => ({
      ...prev,
      selectedProjects: initialSelections
    }));
  };



  // Function to get OAuth access token from credential
  const getAccessToken = async (credential) => {
    if (!credential) {
      throw new Error('No credential available');
    }
    return credential.accessToken;
  };

  const assignBillingAccounts = async (projectIds, destinationBillingAccount) => {


    try {
      // Array to store results of each operation
      const results = [];

      // ensure auth
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user is signed in');
      }
      if (!userCredential?.accessToken) {
        // throw new Error('No access token available. Please sign out and sign in again.');
        await handleGoogleSignIn();
      }
      // Process each project sequentially to handle errors better
      for (const projectId of projectIds) {
        try {
          const response = await fetch(`https://cloudbilling.googleapis.com/v1/projects/${projectId}/billingInfo`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${userCredential.accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              billingAccountName: destinationBillingAccount,
              billingEnabled: true
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to update billing for project ${projectId}: ${response.status}, details: ${errorText}`);
          }

          const result = await response.json();
          results.push({
            projectId,
            success: true,
            billingInfo: result
          });




        } catch (projectError) {
          // Log error but continue with other projects
          console.error(`Error updating billing for project ${projectId}:`, projectError);
          results.push({
            projectId,
            success: false,
            error: projectError.message
          });
        }

        let all_messages = results.map(r =>
        r.success ?
          `✓ Switched billing account for ${r.projectId}` :
          `✗ Error switching billing account for ${r.projectId}`
        );
        setSwitchingProgress(all_messages);


      }

      setCurrentStep('congratulations');
      // Return summary of all operations
      return {
        totalProjects: projectIds.length,
        successfulUpdates: results.filter(r => r.success).length,
        failedUpdates: results.filter(r => !r.success).length,
        details: results
      };

    } catch (error) {
      console.error('Error in billing account assignment:', error);
      throw error;
    }
  };

  
  // Function to list GCP projects
  // const listGCPProjects = async (accessToken) => {
  //   try {
  //     const response = await fetch('https://cloudresourcemanager.googleapis.com/v1/projects', {
  //       headers: {
  //         'Authorization': `Bearer ${accessToken}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });
  //
  //     if (!response.ok) {
  //       const errorText = await response.text();
  //       throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
  //     }
  //
  //     const data = await response.json();
  //
  //     // Filter for active projects only and transform to the required format
  //     const activeProjects = data.projects
  //       ?.filter(project => project.lifecycleState === 'ACTIVE')
  //       .map(project => ({
  //         projectId: project.projectId,
  //         projectName: project.name || project.projectId,
  //       })) || [];
  //
  //     return activeProjects;
  //   } catch (error) {
  //     console.error('Error fetching GCP projects:', error);
  //     throw error;
  //   }
  // };

  // Modified addTestProjects function
  const addTestProjects = async () => {
    setIsLoadingProjects(true);
    setProjectsError(null);
    try {
      const cred = await ensureCredential();
      
      const response = await fetch('https://cloudresourcemanager.googleapis.com/v1/projects', {
        headers: {
          'Authorization': `Bearer ${cred.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
      }

      const data = await response.json();
      console.log(data.projects)

      // Filter for active projects only and transform to the required format
      const activeProjects = data.projects
        ?.filter(project => project.lifecycleState === 'ACTIVE')
          ?.filter(project => !project.projectId.startsWith('sys-'))
        .map(project => ({
          projectId: project.projectId,
          projectName: project.name || project.projectId,
        })) || [];

      // Update the projects state
      setProjects(activeProjects);

      // Initialize all projects as selected in the wizard state
      const initialSelections = {};
      activeProjects.forEach(project => {
        initialSelections[project.projectId] = true;
      });

      setWizardState(prev => ({
        ...prev,
        selectedProjects: initialSelections
      }));
    } catch (error) {
      console.error('Error loading GCP projects:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setProjectsError('Authentication cancelled by user');
      } else if (error.code === 'auth/popup-blocked') {
        setProjectsError('Authentication popup was blocked. Please enable popups for this site.');
      } else {
        setProjectsError(`Failed to load projects: ${error.message}`);
      }
    } finally {
      setIsLoadingProjects(false);
    }
  };





    const renderSignInPage = () => (
      <div className="relative isolate px-6 pt-8 lg:px-8">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] rotate-[30deg] bg-gradient-to-tr from-cyan-200 to-cyan-400 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          />
        </div>
        <div className="mx-auto max-w-2xl ">
          <div className="hidden sm:mb-8 sm:flex sm:justify-center">
            <div className="relative rounded-full px-3 py-1 text-sm/6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
              Google Cloud Premier Partner{' '}
              <span className="font-semibold text-cyan-600">
                Sponsored Program
              </span>
            </div>
          </div>
          <div className="text-left">
            <h1 className="text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
              Google Cloud Benefits Program
            </h1>
            <div className="mt-10 relative rounded-tl-xl border border-gray-200 p-8 shadow-lg">
              <div className="float-right ml-4 mb-2">
                <span className="text-base/7 rounded-tl-xl rounded-br-xl font-semibold text-xl text-black bg-cyan-300 p-3">What is this program?</span>
              </div>
              <p className="text-pretty text-gray-500">
                <strong className="text-black underline decoration-cyan-300 decoration-2 underline-offset-4">Congrats!</strong> As a limited time incentive, Google is offering to pay for your enrollment into the <CompanyName /> Benefits Program for Google Cloud. This gives you access to premium GCP support and certain services at no cost, on top of any benefits you already get from Google.
              </p>
            </div>
            <div>
              <Heading className="mt-8">What do you get?</Heading>
              <dl className="mt-3 grid grid-cols-1 gap-y-4">
                {[
                  {
                    name: '24/7 Advanced Google Cloud Technical Support',
                    description: 'Complimentary, enterprise-grade support from Google Cloud experts around the clock'
                  },
                  {
                    name: 'Broadened access to Google funding',
                    description: 'Eligibility for funding for professional services and undisclosed Google discounts'
                  },
                  {
                    name: 'Streamlined invoice billing', 
                    description: 'Simplified monthly billing from KloudStax with consolidated invoices with Net 15 terms'
                  }
                ].map((feature) => (
                  <div key={feature.name} className="relative pl-9">
                    <dt className="font-semibold text-gray-900">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="absolute left-0 top-1 size-5 text-cyan-500">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      {feature.name}
                    </dt>
                    <dd className="mt-2 text-gray-500">{feature.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
            

             
            </div>
            <div className="relative bg-gradient-to-br from-cyan-100 to-cyan-300">
              <div className="mx-auto max-w-7xl px-6 py-6 mt-8 lg:flex lg:items-center lg:justify-between lg:px-8 text-center lg:text-left">
                <h2 className="max-w-2xl text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                  {currentUser ? 'Continue your enrollment' : 'Ready to get started?'}
                </h2>
                <img
                  src="/arrow.png"
                  alt="Arrow pointing to sign in"
                  className="hidden lg:block w-20 -translate-y-4 -translate-x-2"
                />
                <div className="mt-10 items-center gap-x-6 lg:mt-0 lg:shrink-0 text-center">
                
                  <Button
                    onClick={handleGoogleSignIn}
                    className="bg-gray-700 hover:bg-black text-white flex flex-col items-center rounded-xl cursor-pointer"
                  >

                    
                    {currentUser ? 'Continue where I left off' : 'Sign in with Google'}
                    <div className="text-sm font-normal text-cyan-300">2-minute enrollment</div>
                  </Button>
                  <div className="text-sm/6 font-semibold text-gray-900">
                    No cost - Google sponsored
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
    );

    const TermsOfServiceSection = () => {
      const [tosTimestamp, setTosTimestamp] = useState(null);

      useEffect(() => {
        const fetchTosTimestamp = async () => {
          if (!auth.currentUser) return;
          
          const docRef = doc(db, 'customers', auth.currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const timestamp = docSnap.data()?.provisioning_details?.tos_accepted_at;
            if (timestamp) {
              setTosTimestamp(timestamp.toDate());
            }
          }
        };

        fetchTosTimestamp();
      }, []);

      return (
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-10">
          <div className="text-3xl">
            <div className="block text-5xl font-semibold tracking-tight text-gray-900 mb-2">
              Hi, {splitDisplayName(auth.currentUser.displayName)[0]}! 👋
            </div>
            <span className="text-lg text-gray-500">
              You are logged in with {auth.currentUser.email}
                  &nbsp;(<button 
                onClick={logoutToWelcome}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Not You?
              </button>)
            </span>
          </div>
        </div>
          {/* <div className="rounded-lg border border-zinc-200 bg-white p-6 prose prose-sm max-w-none dark:prose-invert dark:border-zinc-700 dark:bg-zinc-800">
            <ReactMarkdown className="">
              {termsContent}
            </ReactMarkdown>
          </div> */}
          <CheckboxField>
            <Checkbox
              checked={tosTimestamp ? true : wizardState.userAcceptedTerms}
              onChange={handleTermsCheckbox}
              disabled={!!tosTimestamp}
            />
            <Label>
              {tosTimestamp 
                ? `Terms of Service accepted at ${tosTimestamp.toLocaleString()}`
                : <>I have read and agree to the <a href="https://cloud.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800" onClick={(e) => e.stopPropagation()}>GOOGLE CLOUD TERMS OF SERVICE</a> and KloudStax's <a href="https://www.kloudstax.com/platform-agreement" target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800" onClick={(e) => e.stopPropagation()}>PLATFORM AGREEMENT</a></>
              }
            </Label>
          </CheckboxField>
          <Button
            onClick={handleTermsAccept}
            disabled={!tosTimestamp && !wizardState.userAcceptedTerms}
            className="bg-black text-white cursor-pointer disabled:cursor-not-allowed"
          >
            {tosTimestamp ? 'Continue' : 'Accept'}
          </Button>
        </div>
      );
    };

const renderUserForm = () => (
  <EnrollmentForm 
      onSubmit={handleFormSubmit}
      currentUser={currentUser}
      handleGoogleSignIn={handleGoogleSignIn}
      logoutToWelcome={logoutToWelcome}
    />
);


  const renderWhoIsKloudStax = () => (
    <div className="space-y-6">
      <Heading>Who is Kloud<u className="decoration-cyan-300 decoration-3 underline-offset-4">Stax</u>?</Heading>
      <div className="mt-1 text-lg/8 text-gray-600"><CompanyName /> is a certified Premier-tier Google Cloud Partner. <CompanyName /> is regularly audited by Google to ensure the highest standards of service delivery and technical expertise on Google Cloud. They are a leading reseller of Google Cloud products and also provide professional services across AI, Infrastructure, Development, and Data.</div>
    </div>
  );
    const renderProgress = () => (
            <div className="mx-auto max-w-3xl">
            <h4 className="text-pretty text-xl text-center text-gray-600 sm:text-2xl">Your progress</h4>
            <Text className="text-pretty text-sm itacic text-center text-gray-400 mb-5">Total estimated time: 2 minutes</Text>
            {/* --- Progress bar (left */}
            <nav aria-label="Progress">
              <ol role="list" className="overflow-hidden">
                {Object.entries(steps).filter(([_, o]) => !o.hide_from_progress).map(([stepKey, step], stepIdx) => (
                    <li key={step.name} className={classNames(stepIdx !== steps.length - 1 ? 'pb-10' : '', 'relative')}>
                      {step.status === 'complete' ? (
                          <>
                            {stepIdx !== steps.length - 1 ? (
                                <div aria-hidden="true"
                                     className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-cyan-400"/>
                            ) : null}
                            <button 
                              onClick={() => setCurrentStep(stepKey)} 
                              className="group relative flex items-start w-full text-left"
                            >
                    <span className="flex h-9 items-center">
                      <span
                          className="relative z-10 flex size-8 items-center justify-center rounded-full bg-gray-700 group-hover:bg-cyan-500">
                            <CheckIcon aria-hidden="true" className="size-5 text-cyan-300"/>
                          </span>
                    </span>
                              <span className="ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium text-gray-400 italic">{step.name}</span>
                        <span className="text-sm text-gray-400 italic">{step.description}</span>
                      </span>
                            </button>
                          </>
                      ) : step.status === 'current' ? (
                          <>
                            {stepIdx !== steps.length - 1 ? (
                                <div aria-hidden="true"
                                     className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-300"/>
                            ) : null}
                            <a href={step.href} aria-current="step" className="group relative flex items-start">
                    <span aria-hidden="true" className="flex h-9 items-center">
                      <span
                          className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-cyan-400 bg-white">
                            <span className="size-2.5 rounded-full bg-cyan-400"/>
                          </span>
                    </span>
                              <span className="ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium ">{step.name}</span>
                        <span className="text-sm text-gray-500">{step.description}</span>
                      </span>
                            </a>
                          </>
                      ) : (
                          <>
                            {stepIdx !== steps.length - 1 ? (
                                <div aria-hidden="true"
                                     className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-300"/>
                            ) : null}
                            <span className="group relative flex items-start">
                    <span aria-hidden="true" className="flex h-9 items-center">
                      <span
                          className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400">
                            <span className="size-2.5 rounded-full bg-transparent group-hover:bg-gray-300"/>
                          </span>
                    </span>
                              <span className="ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium text-gray-500">{step.name}</span>
                        <span className="text-sm text-gray-500">{step.description}</span>
                      </span>
                            </span>
                          </>
                      )}
                    </li>
                ))}
              </ol>
            </nav>
          </div>

    )
  const renderCongratulations = () => (
    <div className="relative isolate px-6 pt-8 lg:px-8">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
      >
        <div
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
          className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] rotate-[30deg] bg-gradient-to-tr from-cyan-200 to-cyan-400 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
        />
      </div>
      <div className="mx-auto max-w-2xl">
        <div className="text-left">
          <div className="flex items-center justify-center mb-8">
            <div className="size-20 rounded-full bg-gradient-to-tr from-cyan-500 to-cyan-300 flex items-center justify-center">
              <CloudIcon 
                className="size-12 text-white"
              />
            </div>
          </div>
          <h1 className="text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-5xl text-center">
            Welcome to <CompanyName />!
          </h1>
          <div className="mt-10 relative rounded-tl-xl border border-gray-200 p-8 shadow-lg">
            <div className="float-right ml-4 mb-2">
              <span className="text-base/7 rounded-tl-xl rounded-br-xl font-semibold text-xl text-black bg-cyan-300 p-3">What's Next?</span>
            </div>
            <p className="text-pretty text-gray-500">
              <strong>That was the hard part!</strong> In the next few minutes, you should receive an email from <a href="mailto:sales@kloudstax.com" className="text-cyan-600 hover:text-cyan-500">sales@kloudstax.com</a> with any followup steps.
            </p>
          </div>
          <AccountInformationSection provisioningDetails={provisioningDetails} setCurrentStep={setCurrentStep} />
          {switchingProgress.length > 0 && (
            <div>
              <Heading className="mt-8">Project Updates</Heading>
              <dl className="mt-3 grid grid-cols-1 gap-y-4">
                {switchingProgress.map(projectId => (
                  <div key={projectId} className="relative pl-9">
                    <dt className="font-semibold text-gray-900">
                      <svg className="absolute left-0 top-1 size-5 text-cyan-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Project Update Complete
                    </dt>
                    <dd className="mt-2 text-gray-500">Switched billing account for {projectId}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderProjectTransfer = () => (
    <ProjectTransfer />
  );

  const renderAlreadySubmitted = () => (
    <div className="relative isolate px-6 pt-8 lg:px-8">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
      >
        <div
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
          className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] rotate-[30deg] bg-gradient-to-tr from-cyan-200 to-cyan-400 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
        />
      </div>
      <div className="mx-auto max-w-2xl">
        <div className="text-left">
          <div className="flex items-center justify-center mb-8">
            <div className="size-20 rounded-full bg-gradient-to-tr from-cyan-500 to-cyan-300 flex items-center justify-center">
              <CheckIcon 
                className="size-12 text-white"
              />
            </div>
          </div>
          <h1 className="text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-5xl text-center">
            You're already enrolled!
          </h1>
          <div className="mt-10 relative rounded-tl-xl border border-gray-200 p-8 shadow-lg">
            <div className="float-right ml-4 mb-2">
              <span className="text-base/7 rounded-tl-xl rounded-br-xl font-semibold text-xl text-black bg-cyan-300 p-3">Account Details</span>
            </div>
            <p className="text-pretty text-gray-500">
              <strong>Your account is active and ready to use.</strong> If you have questions about your account, please contact <a href="mailto:sales@kloudstax.com" className="text-cyan-600 hover:text-cyan-500">sales@kloudstax.com</a>.
            </p>
          </div>
          <AccountInformationSection provisioningDetails={provisioningDetails} setCurrentStep={setCurrentStep} />
        </div>
      </div>
    </div>
  );

  const renderProvisioning = () => (
    <div className="relative isolate px-6 pt-14 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-cyan-200 to-cyan-400 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          />
        </div>

        <div className="flex flex-col items-center">
          <div className="animate-bounce mb-8">
            <div className="size-16 rounded-full bg-gradient-to-tr from-cyan-500 to-cyan-300 flex items-center justify-center">
              <svg 
                className="size-8 text-white" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 10l7-7m0 0l7 7m-7-7v18" 
                />
              </svg>
            </div>
          </div>

          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
            Setting up your account
          </h1>
          
          <div className="mt-10 flex items-center justify-center gap-x-6">
            {provisioningStatus === 'processing' && (
              <div className="relative">
                <div className="size-12 rounded-full border-4 border-cyan-300 border-t-transparent animate-spin transition-opacity duration-300 opacity-100 group-hidden:opacity-0" />
              </div>
            )}
            
          </div>

          <div className="mt-10 max-w-xl">
            <div className="rounded-lg bg-white/5 shadow-lg ring-1 ring-black/5 p-8">
              <div className="flex flex-col gap-4">
                {(!provisioningDetails?.steps || provisioningDetails?.steps.length === 0) ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 size-5 rounded-full  flex items-center justify-center">
                      <div className="size-3 rounded-full border-2 border-cyan-300 border-t-transparent animate-spin" />
                    </div>
                    <span className="text-sm text-gray-500">
                      Initializing setup...
                    </span>
                  </div>
                ) : (
                  provisioningDetails.steps.map((step, index) => {
                    const isInProgress = !step.done && !provisioningDetails.steps.slice(0, index).some(s => !s.done);
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <div className={`flex-shrink-0 size-5 rounded-full ${
                          step.done ? 'bg-cyan-400' : 'bg-gray-200'
                        } flex items-center justify-center`}>
                          {step.done ? (
                            <CheckIcon className="size-3 text-white animate-[checkmark_0.4s_ease-in-out]" />
                          ) : provisioningStatus === 'failed' ? (
                            <XMarkIcon className="size-4 text-red-500" />
                          ) : isInProgress && (
                            <div className="size-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
                          )}
                        </div>
                        <span className={`text-sm ${
                          step.done ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {step.name}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {provisioningStatus === 'failed' && (
            <div className="mt-8 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Sorry! There was an error setting up your account. KloudStax has been notified, and will reach out to you over email.
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      For urgent assistance with this, contact us at{' '}
                      <a href="mailto:sales@kloudstax.com" className="font-medium underline">
                        sales@kloudstax.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  let steps = {
    welcome: { name: 'Welcome', description: 'What is this about?', href: '#', status: 'complete', hide_from_progress: false, view: renderSignInPage},
    'gmail-block': { 
      name: 'Company Email Required', 
      description: 'Switch to your company email', 
      href: '#', 
      status: 'current', 
      hide_from_progress: true, 
      view: () => <GmailBlockPage currentUser={currentUser} logoutToWelcome={logoutToWelcome} handleGoogleSignIn={handleGoogleSignIn} />
    },
    terms: { name: 'Terms of Service', description: 'Accept the KloudStax reseller Terms of Service', href: '#', status: 'current', hide_from_progress: false, view: () => <TermsOfServiceSection /> },
    form: { name: 'Company Information', description: 'Name, address, and contact information', href: '#', status: 'upcoming',  hide_from_progress: false, view: renderUserForm },
    provisioning: { 
      name: 'Setting up your account', 
      description: 'Creating your new billing account', 
      href: '#', 
      status: 'upcoming', 
      hide_from_progress: false, 
      view: renderProvisioning 
    },
    congratulations: { name: 'All done!', description: 'Check your email for a summary & any next steps', href: '#', status: 'upcoming', hide_from_progress: false, view: renderCongratulations },
    invoiced_setup: { 
      name: 'Setup Billing', 
      description: 'Add a payment method for your account.', 
      href: '#', 
      status: 'upcoming', 
      hide_from_progress: true,  // for now
      view: () => <InvoicedSetup currentUser={currentUser} onComplete={() => setCurrentStep('congratulations')} />
    },
    done: { name: 'Already Submitted', description: '', href: '#', status: 'upcoming', hide_from_progress: true, view: renderAlreadySubmitted },
    transfer_projects: { name: '(Optional) Transfer Projects', description: 'Automatically point projects to your new account', href: '#', status: 'upcoming', hide_from_progress: true, view: renderProjectTransfer }
  };
  let reachedCurrent = false;
    for (const key of Object.keys(steps)) {
        if (reachedCurrent) break; // Exit loop once we reach `currentStep`

        if (key === currentStep) {
            steps[key].status = "current";
            reachedCurrent = true;
        } else {
            steps[key].status = "complete";
        }
    }
    
    const renderContactCards = () => {
      if (isLoadingTeamMembers) {
        return (
          <div className="bg-zinc-900 mb-5 rounded-tl-3xl rounded-br-3xl shadow-xl p-3 py-6">
            <div className="animate-pulse flex space-y-4">
              <div className="flex-1 space-y-4 py-1">
                <div className="h-4 bg-zinc-700 rounded w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-zinc-700 rounded"></div>
                  <div className="h-4 bg-zinc-700 rounded w-5/6"></div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      if (teamMembersError) {
        return (
          <div className="bg-zinc-900 mb-5 rounded-tl-3xl rounded-br-3xl shadow-xl p-3 py-6">
            <div className="text-red-400">
              Error loading team members: {teamMembersError}
            </div>
          </div>
        );
      }

      return (
        <div className="bg-zinc-900 mb-5 rounded-tl-3xl rounded-br-3xl shadow-xl p-3 py-6">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0">
              <h2 className="text-pretty text-4xl font-semibold tracking-tight text-cyan-300 sm:text-5xl">Meet your team</h2>
              <p className="mt-6 text-lg/8 text-zinc-400">
                Google and <CompanyName /> work as a unified team to bring you a first-class experience on Google Cloud.
              </p>
            </div>
            <ul
              role="list"
              className="mx-auto mt-5 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-16 text-center"
            >
              {teamMembers.map((person) => (
                <li key={person.name}>
                  {person.imageUrl ? (
                    <img alt="" src={person.imageUrl} className="mx-auto size-24 rounded-full" />
                  ) : (
                    <span className="inline-flex size-24 items-center justify-center rounded-full bg-gray-500">
                      <span className="text-3xl font-medium text-white">
                        {person.name.split(' ')
                          .filter((_, i, arr) => i === 0 || i === arr.length - 1)
                          .map(word => word.charAt(0).toUpperCase())
                          .join('')}
                      </span>
                    </span>
                  )}
                  <h3 className="mt-6 text-base/7 font-semibold tracking-tight text-white">{person.name}</h3>
                  <p className="text-sm/6 text-zinc-400 whitespace-pre-line">{person.role}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )
    }

// Layout components
const PageLayout = ({ children }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [content, setContent] = useState(children);
  const [oldContent, setOldContent] = useState(null);

  useEffect(() => {
    setIsVisible(false);
    setOldContent(content);
    
    const timer = setTimeout(() => {
      setContent(children);
      setIsVisible(true);
      setOldContent(null);
    }, 300);

    return () => clearTimeout(timer);
  }, [children]);

  return (
    <div className="min-h-screen xxbg-zinc-50 dark:bg-zinc-900">
      <header className="bg-white shadow-sm dark:bg-zinc-800">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <img 
              src={`${process.env.PUBLIC_URL}/ks-logo-full-black.png`} 
              alt="KloudStax Logo" 
              className="h-12 w-auto" 
            />
            <img 
              src={`${process.env.PUBLIC_URL}/google-cloud-logo.png`} 
              alt="Google Cloud Logo" 
              className="h-12 w-auto" 
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {content}
          </div>
          <div className="md:col-span-1">
            {renderContactCards()}
            <Divider className="mt-10 mb-5" soft />
            {currentStep === 'congratulations' ? null : 
             currentStep === 'welcome' ? renderWhoIsKloudStax() :
             renderProgress()}
          </div>
        </div>
      </main>

      <footer className="bg-white shadow-sm dark:bg-zinc-800 mt-auto">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <Text className="text-center text-sm text-zinc-500">
            © 2026 <CompanyName />. All rights reserved.
          </Text>
        </div>
      </footer>
    </div>
  );
};

  useEffect(() => {
    if (currentStep !== 'provisioning' || !currentUser) return;

    const unsubscribe = onSnapshot(
      doc(db, 'customers', currentUser.uid),
      (doc) => {
        const provisioningDetails = doc.data()?.provisioning_details;
        const status = provisioningDetails?.status;
        setProvisioningStatus(status);
        setProvisioningDetails(provisioningDetails);
        
        if (status === 'completed') {
          // Add a small delay before transitioning to give user time to see completion
          setTimeout(() => {
            setCurrentStep('congratulations');
          }, 1500);
        }
      }
    );

    return () => unsubscribe();
  }, [currentStep, currentUser]);

  // Add new useEffect for scrolling
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Add new useEffect to fetch team members when user logs in
  useEffect(() => {
    

    const fetchTeamMembers = async () => {
      try {
        const functions = getFunctions();
        const getTeamMembers = httpsCallable(functions, 'getTeamMembers');
        
        // Extract FSR and KS owner from URL path segments
        const pathSegments = window.location.pathname.substring(1).split('/');
        
        // Check if first segment starts with @ for ksOwner
        let fsr = null;
        let ksOwner = null;
        
        if (pathSegments[0]?.startsWith('@')) {
          ksOwner = pathSegments[0].substring(1); // Remove @ symbol
          fsr = pathSegments[1] || null;
        } else {
          fsr = pathSegments[0] || null;
          // Check second segment for ksOwner
          if (pathSegments[1]?.startsWith('@')) {
            ksOwner = pathSegments[1].substring(1);
          } else {
            ksOwner = pathSegments[1] || null;
          }
        }
        
        const result = await getTeamMembers({ 
          customer_domain: currentUser ? currentUser.email.split('@')[1] : null,
          fsr: fsr,
          ksOwner: ksOwner
        });
        
        setTeamMembers(result.data.people);
      } catch (error) {
        console.error('Error fetching team members:', error);
        setTeamMembers(DEFAULT_TEAM_MEMBERS);
      } finally {
        setIsLoadingTeamMembers(false);
      }
    };

    fetchTeamMembers();
  }, [currentUser]); // Only re-run if currentUser changes

  // Add this effect to identify users after login
  useEffect(() => {
    if (currentUser) {
      unify.identify(currentUser.email);
    }
  }, [currentUser, unify]);

  return <PageLayout>
    {steps[currentStep]['view']()}
  </PageLayout>;
};

// Wrap App with AuthProvider
const AppWithAuth = () => (
  <Router>
    <UnifyIntentProvider intentClient={intentClient}>
      <AuthProvider>
        <Routes>
          <Route path="/doc-preview" element={<DocPreview />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/transfer" element={<ProjectTransfer />} />
          <Route path="/*" element={<App />} />
        </Routes>
      </AuthProvider>
    </UnifyIntentProvider>
  </Router>
);

const AccountInformationSection = ({ provisioningDetails, setCurrentStep }) => (
  <div className="mt-8">
    <Heading>Account Information</Heading>
    <dl className="mt-3 grid grid-cols-1 gap-y-4">
      <div className="relative pl-9">
        <dt className="font-semibold text-gray-900">
          <CloudIcon aria-hidden="true" className="absolute left-0 top-1 size-5 text-cyan-500"/>
          Billing Account ID
        </dt>
        <dd className="mt-2 text-gray-500">{provisioningDetails?.billingAccountId?.replace('billingAccounts/', '') || 'Not available'}
        {provisioningDetails?.billingAccountId?.replace('billingAccounts/', '') && (
          <Text className="text-gray-500">
            <a 
              href={`https://console.cloud.google.com/billing/${provisioningDetails?.billingAccountId?.replace('billingAccounts/', '')}`}
              className="text-cyan-600 hover:text-cyan-500 underline inline-flex items-center gap-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Cloud Console
              <ArrowTopRightOnSquareIcon className="size-4" aria-hidden="true" />
            </a>
          </Text>
        )}
        </dd>
      </div>
      <div className="relative pl-9">
        <dt className="font-semibold text-gray-900">
          <ClockIcon aria-hidden="true" className="absolute left-0 top-1 size-5 text-cyan-500"/>
          Provisioning Status
        </dt>
        <dd className="mt-2 text-gray-500">
          Completed at {provisioningDetails?.lastUpdated?.toDate().toLocaleString() || 'Not available'}
        </dd>
      </div>
      <div className="relative pl-9">
        <dt className="font-semibold text-gray-900">
          <DocumentTextIcon aria-hidden="true" className="absolute left-0 top-1 size-5 text-cyan-500"/>
          Terms of Service
        </dt>
        <dd className="mt-2">
          <a 
            href="/terms" 
            className="text-cyan-600 hover:text-cyan-500 underline inline-flex items-center gap-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://get.kloudstax.com/terms
            <ArrowTopRightOnSquareIcon className="size-4" aria-hidden="true" />
          </a>
          <Text>{provisioningDetails?.tos_accepted_at ? `Accepted at ${provisioningDetails?.tos_accepted_at?.toDate().toLocaleString()}` : ''}</Text>
        </dd>
      </div>
      {/* New section for Payment Method Status */}
      <div className="relative pl-9">
        <dt className="font-semibold text-gray-900">
          <BanknotesIcon aria-hidden="true" className="absolute left-0 top-1 size-5 text-cyan-500"/>
          Payment Method
        </dt>
        <dd className="mt-2 text-gray-500">
          {provisioningDetails?.billingSetupComplete ? (
            <span className="inline-flex items-center gap-x-1.5 rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
              <svg className="h-1.5 w-1.5 fill-green-500" viewBox="0 0 6 6" aria-hidden="true">
                <circle cx={3} cy={3} r={3} />
              </svg>
              Configured
            </span>
          ) : (
             <span className="inline-flex items-center gap-x-1.5 rounded-md bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
              <svg className="h-1.5 w-1.5 fill-yellow-500" viewBox="0 0 6 6" aria-hidden="true">
                <circle cx={3} cy={3} r={3} />
              </svg>
              Pending Setup
            </span>
          )}
        </dd>
      </div>
    </dl>
    {/* Updated button section -- HIDDEN FOR NOW */}
    <div className="hidden mt-8 flex flex-col sm:flex-row gap-4">
      {/* Conditionally render the setup button */}
      {!provisioningDetails?.billingSetupComplete && (
        <Button color="black" onClick={() => setCurrentStep('invoiced_setup')} className="flex-1">
            Setup Automatic Payments
        </Button>
      )}
      <Button outline onClick={() => setCurrentStep('transfer_projects')} className="flex-1">
        Optional: Move Projects to your KloudStax Billing Account
      </Button>
    </div>
  </div>
);

// Add this function to check if email is a Gmail account
const isGmailAccount = (email) => {
  return /@g(oogle)?mail\.com$/.test(email);
};

// Add this constant with your other constants
const writeKey = process.env.REACT_APP_UNIFY_API_KEY;

const config = {
  autoPage: true,
  autoIdentify: false,
};

const intentClient = new UnifyIntentClient(writeKey, config);

export const ConfirmationModal = ({ 
  open, 
  onClose,
  title,
  message,
  primaryButtonText = "Confirm",
  secondaryButtonText = "Cancel",
  onPrimaryAction,
  onSecondaryAction,
  icon = <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-yellow-600" />,
  iconBgColor = "bg-cyan-100"
}) => {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div>
              <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900 text-center">
                {title}
              </DialogTitle>
              <div className="mt-4">
                <div className="flex gap-4">
                  <div className={`flex-shrink-0 flex size-10 items-center justify-center rounded-full ${iconBgColor}`}>
                    {icon}
                  </div>
                  <div className="flex-grow">
                    {message}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                onClick={onPrimaryAction}
                className="inline-flex w-full justify-center rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:text-cyan-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600 sm:col-start-2"
              >
                {primaryButtonText}
              </button>
              <button
                type="button"
                data-autofocus
                onClick={onSecondaryAction}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:ring-gray-400 sm:col-start-1 sm:mt-0"
              >
                {secondaryButtonText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}


// Final component with Card and Bank Account options

const CardForm = ({ cardData, handleInputChange }) => (
  <FieldGroup className="space-y-4">
    <Field>
      <Label>Name on Card</Label>
      <Input name="name" value={cardData.name} onChange={handleInputChange} required />
    </Field>
    <Field>
      <Label>Card Number</Label>
      <Input name="number" value={cardData.number} onChange={handleInputChange} placeholder="•••• •••• •••• ••••" required />
    </Field>
    <div className="grid grid-cols-2 gap-4">
      <Field>
          <Label>Expiry (MM/YY)</Label>
          <Input name="expiry" value={cardData.expiry} onChange={handleInputChange} placeholder="MM / YY" required />
      </Field>
      <Field>
          <Label>CVC</Label>
          <Input name="cvc" value={cardData.cvc} onChange={handleInputChange} placeholder="•••" required />
      </Field>
    </div>
  </FieldGroup>
);

const BankForm = ({ bankData, handleInputChange }) => (
  <FieldGroup className="space-y-4">
      <Field>
          <Label>Account Holder Name</Label>
          <Input name="name" value={bankData.name} onChange={handleInputChange} required />
      </Field>
      <Field>
          <Label>Account Type</Label>
          <div className="flex gap-x-6 mt-2">
              <div className="flex items-center gap-x-2">
                  <input id="individual" name="type" type="radio" value="individual" checked={bankData.type === 'individual'} onChange={handleInputChange} className="h-4 w-4 border-gray-300 text-cyan-600 focus:ring-cyan-600"/>
                  <label htmlFor="individual" className="block text-sm font-medium leading-6 text-gray-900">Personal</label>
              </div>
              <div className="flex items-center gap-x-2">
                  <input id="company" name="type" type="radio" value="company" checked={bankData.type === 'company'} onChange={handleInputChange} className="h-4 w-4 border-gray-300 text-cyan-600 focus:ring-cyan-600"/>
                  <label htmlFor="company" className="block text-sm font-medium leading-6 text-gray-900">Business</label>
              </div>
          </div>
      </Field>
      <Field>
          <Label>Routing Number</Label>
          <Input name="routingNumber" value={bankData.routingNumber} onChange={handleInputChange} required />
      </Field>
      <Field>
          <Label>Account Number</Label>
          <Input name="accountNumber" value={bankData.accountNumber} onChange={handleInputChange} required />
      </Field>
  </FieldGroup>
);

const InvoicedSetup = ({ currentUser, onComplete }) => {
  const [paymentMethodType, setPaymentMethodType] = useState('card'); // 'card' or 'bank'
  const [isInvoicedReady, setIsInvoicedReady] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  
  // State for both forms
  const [cardData, setCardData] = useState({ name: '', number: '', expiry: '', cvc: '' });
  const [bankData, setBankData] = useState({ name: '', type: 'individual', routingNumber: '', accountNumber: '' });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const INVOICED_PUBLIC_KEY = process.env.REACT_APP_INVOICED_PUBLIC_KEY;
  const INVOICED_SANDBOX = process.env.NODE_ENV !== 'production'; 

  useEffect(() => {
    if (document.querySelector('script[src="https://js.invoiced.com/invoiced.js"]')) {
      if (window.Invoiced) setIsScriptLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.invoiced.com/invoiced.js';
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => setError('Failed to load the billing setup script. Please refresh the page.');
    document.body.appendChild(script);
    return () => {
      const el = document.querySelector('script[src="https://js.invoiced.com/invoiced.js"]');
      if (el) document.body.removeChild(el);
    };
  }, []);

  useEffect(() => {
    if (isScriptLoaded && !isInvoicedReady) {
      try {
        // window.Invoiced.setPublishableKey(INVOICED_PUBLIC_KEY, INVOICED_SANDBOX ? 'staging' : 'prod');
        setIsInvoicedReady(true);
      } catch (e) {
        console.error("Error setting Invoiced public key:", e);
        setError("Could not configure the billing service.");
      }
    }
  }, [isScriptLoaded, isInvoicedReady, INVOICED_PUBLIC_KEY, INVOICED_SANDBOX]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (paymentMethodType === 'card') {
      setCardData(prev => ({ ...prev, [name]: value }));
    } else {
      setBankData(prev => ({ ...prev, [name]: value }));
    }
  };

  const tokenizeCard = () => {
    return new Promise((resolve, reject) => {
      const [exp_month, exp_year_short] = cardData.expiry.split('/').map(s => s.trim());
      const exp_year = exp_year_short && exp_year_short.length === 2 ? `20${exp_year_short}` : exp_year_short;
      if (!exp_month || !exp_year) {
        return reject(new Error("Expiration date is invalid. Please use MM/YY format."));
      }
      window.Invoiced.card.tokenize(cardData.number, cardData.cvc, exp_month, exp_year, cardData.name, null, (status, response) => {
        if (status === 200) resolve(response);
        else reject(new Error(response.message || 'An unknown error occurred.'));
      });
    });
  };
  
  const tokenizeBankAccount = () => {
    return new Promise((resolve, reject) => {
      window.Invoiced.bankAccount.tokenize(bankData.name, bankData.type, bankData.accountNumber, bankData.routingNumber, (status, response) => {
          if (status === 200) resolve(response);
          else reject(new Error(response.message || 'An unknown error occurred.'));
      });
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isInvoicedReady || isLoading || success) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = paymentMethodType === 'card'
        ? await tokenizeCard()
        : await tokenizeBankAccount();

      const functions = getFunctions();
      const saveInvoicedPaymentMethod = httpsCallable(functions, 'saveInvoicedPaymentMethod');
      await saveInvoicedPaymentMethod({ invoicedToken: token.id });

      setSuccess(true);
      const docRef = doc(db, 'customers', currentUser.uid);
      await updateDoc(docRef, {
        'provisioning_details.billingSetupComplete': true,
        'provisioning_details.lastUpdated': serverTimestamp()
      });

      setTimeout(() => onComplete(), 3000);
    } catch (e) {
      console.error('Invoiced tokenization or backend call failed:', e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="rounded-lg bg-white p-10 shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
        <Heading>Setup Automatic Payments</Heading>
        {INVOICED_SANDBOX ? <Text className="text-red-500">Invoiced is in sandbox mode.</Text> : null}
        
        <Text className="mt-2 text-gray-500">
          Add a payment method for seamless monthly billing. Your information is tokenized securely and never touches our servers.
        </Text>

        {/* --- TABS --- */}
        <div className="border-b border-gray-200 mt-6 mb-8">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                    onClick={() => setPaymentMethodType('card')}
                    className={classNames(
                        paymentMethodType === 'card' ? 'border-cyan-500 text-cyan-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                        'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium'
                    )}
                >
                    Credit Card
                </button>
                <button
                    onClick={() => setPaymentMethodType('bank')}
                    className={classNames(
                        paymentMethodType === 'bank' ? 'border-cyan-500 text-cyan-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                        'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium'
                    )}
                >
                    Direct Debit (ACH)
                </button>
            </nav>
        </div>
        
        {success ? (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Payment Method Saved!</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Your payment method has been successfully added. You will be redirected shortly.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            
            {paymentMethodType === 'card' ? 
              <CardForm cardData={cardData} handleInputChange={handleInputChange} /> : 
              <BankForm bankData={bankData} handleInputChange={handleInputChange} />
            }

            {error && (
              <div className="rounded-md bg-red-50 p-4 mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">There was an error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row-reverse justify-between gap-4 pt-8">
                <Button 
                    type="submit" 
                    color="black"
                    disabled={isLoading || !isInvoicedReady}
                    className="flex-1"
                >
                    {isLoading ? 'Saving...' : 'Save Payment Method'}
                </Button>
                <Button type="button" onClick={onComplete} plain className="flex-1">
                  Skip for now
                </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};


export default AppWithAuth;